import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { Params } from '@angular/router';
import {
  APPLICATION_DOCUMENT_TYPE,
  BUCKET,
  Option,
  SelectionOption,
} from '@univacity/shared/types';
import { ObjectUtil } from '@univacity/shared/util';
import { differenceInCalendarDays } from 'date-fns';
import { NzDateMode } from 'ng-zorro-antd/date-picker';
import { NzFormTooltipIcon } from 'ng-zorro-antd/form';
import { NzSelectModeType } from 'ng-zorro-antd/select';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  mergeMap,
  of,
  takeUntil,
} from 'rxjs';
import { IconType, Size } from '../../interfaces';
import { AutoComplete, InputType } from '../../interfaces/form';
import { CustomValidators } from '../../validators';
import { DestroyAbleComponent } from '../destroyable/destroyable.component';

@Component({
  selector: 'un-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class InputComponent
  extends DestroyAbleComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Input() type: InputType = 'text';
  @Input() layout: 'horizontal' | 'vertical' = 'vertical';
  @Input() size: Size = 'medium';

  @Input() placeholder = '';
  @Input() label = '';
  @Input() required?: boolean;
  @Input() rows?: number;
  @Input() options: SelectionOption[] = [];
  @Input() max: number;
  @Input() min: number;
  @Input() maxLength?: number;
  @Input() minLength?: number;
  @Input() step = 1;
  @Input() selectionIconTheme?: IconType;
  @Input() prefixIcon?: string;
  @Input() suffixIcon?: string;
  @Input() prefixText?: string;
  @Input() suffixText?: string;
  @Input() showClearIcon = true;
  @Input() disabled?: boolean;
  @Input() formControl?: FormControl | null;
  @Input() formControlName: string;
  @Input() showSearch?: boolean;
  @Input() autoComplete: AutoComplete;
  @Input() autoCompleteQuery: Params;
  @Input() autoCompleteType: 'text' | 'select' = 'select';
  @Input() selectMode: NzSelectModeType = 'default';
  @Input() dateMode: NzDateMode = 'date';
  @Input() dateFormat = 'yyyy-MM-dd';
  @Input() minDate?: Date;
  @Input() maxDate?: Date;

  @Input() showFeedback?: boolean;
  @Input() showErrorMessage = true;
  @Input() status: 'warning' | 'error' | 'success';
  @Input() labeInfoText?: string;
  @Input() labelInfoIcon: string | NzFormTooltipIcon = {
    type: 'question-circle',
    theme: 'fill',
  };
  @Input() infoText?: string;
  @Input() successText?: string;
  @Input() warningText?: string;

  @Input() bucket: BUCKET = BUCKET.private;
  @Input() multiple?: boolean;
  @Input() documentType?: APPLICATION_DOCUMENT_TYPE;
  @Input() documentTypes: Option[] = ObjectUtil.enumToKeyLabelValuePair(
    APPLICATION_DOCUMENT_TYPE
  );
  @Input() accept?: string = '.pdf, .jpg, .png, .jepg, .svg';
  @Input() recommendedSize?: [number, number];
  @Input() uploadType: 'drag' | 'photo' | 'icon' = 'drag';
  @Input() rounded?: boolean;
  @Input() fileValue?: 'full' | 'src' = 'src';

  private searchChange$ = new BehaviorSubject<string>('');
  private lastSearch: string;
  private nextPage?: number | null;

  inputFocus?: boolean;
  value?: any = '';
  control: FormControl<any>;

  isLoading: boolean;
  isLoadingMore: boolean;
  checkOptions: Array<Option & { checked?: boolean }>;
  @Input() editorConfig?: object;

  get _options() {
    return (this.options || []).map((o) => {
      if (typeof o === 'string') return { label: o, value: o };
      return o;
    }) as Option[];
  }

  get _isRequired() {
    return this.control && this.control.hasValidator(Validators.required);
  }

  disableDate = (date: Date) => {
    if (this.minDate && this.maxDate)
      return (
        differenceInCalendarDays(date, this.minDate) < 0 ||
        differenceInCalendarDays(date, this.maxDate) > 0
      );
    else if (this.minDate)
      return differenceInCalendarDays(date, this.minDate) < 0;
    else if (this.maxDate)
      return differenceInCalendarDays(date, this.maxDate) > 0;
    return false;
  };
  constructor(
    @Optional() private controlContainer: ControlContainer,
    private ch: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.setControl();
    this.setValidators();
    this.observeAutocomplete();
    this.setCheckOptions();
    this.initEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (typeof this.autoCompleteQuery !== 'undefined')
      this.observeAutocomplete();
  }

  initEditor() {
    if (!this.editorConfig)
      this.editorConfig = {
        placeholder: this.placeholder || $localize`Click Here Edit`,
        tabsize: 4,
        minHeight: 200,
        maxHeight: '60vh',
        spellCheck: true,
        disableGrammar: false,
        tabDisable: true,
        codeviewFilter: false,
        codeviewIframeFilter: true,
        styleTags: ['p', 'h1', 'h2', 'h3', 'h4', 'h5'],
        toolbar: [
          ['fontsize', ['style', 'fontsize']],
          ['style', ['bold', 'italic', 'underline', 'strikethrough']],
          ['para', ['ul', 'ol', 'paragraph']],
          ['insert', ['table', 'hr']],
          ['insert', ['link', 'picture', 'video', 'help']],
        ],
      };
  }
  private setValidators() {
    if (!this.control) return;
    if (this.maxLength)
      this.control.addValidators([Validators.maxLength(this.maxLength)]);
    if (this.minLength)
      this.control.addValidators([Validators.minLength(this.minLength)]);
    if (this.min) this.control.addValidators([Validators.min(this.min)]);
    if (this.max) this.control.addValidators([Validators.max(this.max)]);
    if (this.type === 'email')
      this.control.addValidators([CustomValidators.email]);
  }
  setControl() {
    if (this.formControl) this.control = this.formControl;
    else if (this.formControlName && this.controlContainer)
      this.control = this.controlContainer.control?.get(
        this.formControlName
      ) as FormControl;
    this.value = this.control?.value;
  }

  private setCheckOptions() {
    if (this.type === 'checkbox') {
      this.checkOptions = JSON.parse(JSON.stringify(this._options));
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateChange = (_: any) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
    if (!this.control) this.control = new FormControl(value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  reset() {
    this.control.reset();
  }

  showErrorIcon() {
    return (
      this.control.errors &&
      Object.keys(this.control.errors).some((k) =>
        [
          'required',
          'email',
          'min',
          'max',
          'minLength',
          'maxLength',
          'password',
          'fileRequired',
        ].includes(k)
      )
    );
  }

  keyUp($e: string) {
    if ($e) this.search($e);
    if (this.autoCompleteType === 'text') this.propagateChange($e);
  }

  search(search: string) {
    if (this.type === 'auto-complete') {
      this.isLoading = true;
      this.lastSearch = search;
      this.searchChange$.next(search);
    }
  }

  loadMore() {
    if (this.nextPage && this.autoComplete) {
      this.isLoadingMore = true;
      this.autoComplete(this.lastSearch, this.nextPage, this.autoCompleteQuery)
        .pipe(
          takeUntil(this.dead$),
          debounceTime(500),
          catchError(() => {
            this.isLoadingMore = false;
            return of({ options: [], nextPage: null });
          })
        )
        .subscribe(({ options, nextPage }) => {
          this.isLoadingMore = false;
          this.options?.push(...options);
          this.nextPage = nextPage;
        });
    }
  }
  private observeAutocomplete() {
    if (this.type === 'auto-complete') {
      this.searchChange$
        .pipe(
          takeUntil(this.dead$),
          debounceTime(500),
          mergeMap(
            (v) =>
              this.autoComplete &&
              this.autoComplete(v, 1, this.autoCompleteQuery)
          ),
          catchError(() => {
            this.isLoading = false;
            return of({ options: [], nextPage: null });
          })
        )
        .subscribe(({ options, nextPage }) => {
          this.isLoading = false;
          this.options = options || [];
          this.nextPage = nextPage;
          this.ch.detectChanges();
        });
      this.search(this.control?.value || '');
    }
  }

  checkboxChange($event: Array<Option & { checked: boolean }>) {
    this.propagateChange($event.filter((v) => v.checked).map((v) => v.value));
  }
  writeValueNonReactive(val: any) {
    this.propagateChange(this.type === 'number' ? +val : val);
  }
}
